import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell, UserPlus, MessageCircle, Star, ThumbsUp, CreditCard } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";

const TYPE_ICON = {
  follow: { I: UserPlus, color: "#618DB8", text: (n) => `${n.actor?.name || "Quelqu'un"} a commencé à vous suivre.` },
  comment: { I: MessageCircle, color: "#10b981", text: (n) => `${n.actor?.name || "Quelqu'un"} a commenté votre publication.` },
  recommendation: { I: Star, color: "#FFB400", text: (n) => `${n.actor?.name || "Un patient"} vous a noté ${n.payload?.rating || 5}★.` },
  like: { I: ThumbsUp, color: "#FF7A5C", text: (n) => `${n.actor?.name || "Quelqu'un"} a aimé votre publication.` },
  card_reminder: { I: CreditCard, color: "#FF7A5C", text: (n) => `Téléconsultation demain avec ${n.actor?.name || "votre praticien"} : enregistrez votre carte pour confirmer (${n.payload?.fee_eur ?? 25} € débités après la consultation).` },
};

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}j`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const loadCount = async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count || 0); } catch { /* silent */ }
  };
  const loadList = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch { /* silent */ }
  };

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openDropdown = async () => {
    setOpen(true);
    await loadList();
    if (count > 0) {
      try { await api.post("/notifications/read"); setCount(0); } catch { /* silent */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={open ? () => setOpen(false) : openDropdown}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#618DB8] transition"
        title="Notifications" data-testid="bell-btn">
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-1 -end-1 bg-[#FF7A5C] text-white text-[10px] font-bold rounded-full px-1.5 min-w-4 h-4 flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute end-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="bell-dropdown">
          <div className="px-4 py-3 border-b border-slate-100 font-display font-extrabold text-deep text-sm">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">Aucune notification.</div>
            ) : items.map((n) => {
              const t = TYPE_ICON[n.type] || { I: Bell, color: "#94a3b8", text: () => n.type };
              return (
                <Link key={n.notification_id} to={n.actor ? `/u/${n.actor.user_id}` : "/dashboard/feed"}
                  onClick={() => setOpen(false)}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-50 ${n.read ? "" : "bg-[#618DB8]/5"}`}
                  data-testid={`notif-${n.notification_id}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: t.color + "20" }}>
                    <t.I className="h-4 w-4" style={{ color: t.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-700 flex items-center gap-1 flex-wrap">
                      {t.text(n)}{n.actor?.blue_check && <VerifiedBadge size={10} />}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
